import { useBasicQuery } from '@hooks/requests/utils/use_basic_query';
import { PARTNER_BILLING_BULK_PATH } from '@hooks/requests/utils/paths';
import type { DateRangeValue } from '../../../components/ui';

export type BillingTrendPoint = {
  /** YYYY-MM */
  month: string;
  /** "Jan 26" */
  label: string;
  /** Sum of maxUserCount across all companies for this month */
  maxUserCount: number;
};

/** A single (company, month) billing entry, as returned by the bulk endpoint. */
export interface PartnerBillingBulkResult {
  organizationId: string;
  companyId: string | null;
  billingMonth: string;
  maxUserCount: number;
  currentActiveCount: number;
  totalEvents: number;
}

interface PartnerBillingBulkResponse {
  results: PartnerBillingBulkResult[];
}

const toYYYYMM = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

/** Build an array of YYYY-MM strings for the last `count` months (oldest → newest). */
const buildMonths = (count: number): string[] => {
  const now = new Date();
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (count - 1 - i), 1);
    return toYYYYMM(d);
  });
};

/**
 * Exact months covered by `dateRange.from` → `dateRange.to`, with no padding
 * and no trailing context — just the selected period itself. Used to scope
 * period-bound widgets (e.g. the "High-water Mark" card) to precisely what's
 * selected, as distinct from `resolveMonths` below, which pads single-month
 * presets out to a trailing window for the chart's benefit. `dateRange` is
 * required here (unlike `resolveMonths`) — callers decide what "nothing
 * selected" means for their own widget.
 */
export const exactMonthsInRange = (dateRange: DateRangeValue): string[] => {
  const { from, to } = dateRange;
  const months: string[] = [];
  const cur = new Date(from.getFullYear(), from.getMonth(), 1);
  const end = new Date(to.getFullYear(), to.getMonth(), 1);
  while (cur <= end) {
    months.push(toYYYYMM(cur));
    cur.setMonth(cur.getMonth() + 1);
  }
  return months;
};

/**
 * Resolve which months to fetch from a dateRange value — this drives the
 * *chart's* trend data, which always wants surrounding context rather than
 * a single dot.
 *
 * - null / undefined                          → last `defaultCount` months ending at current month
 * - single-month presets (current_month,
 *   last_month, month)                        → last `defaultCount` months ending at the selected month
 * - multi-month range (last_two_months,
 *   last_three_months, on_range)              → exactly the months covered by from…to, in full —
 *   e.g. "Last two months" shows exactly those 2 months, "Last three months" exactly those 3, and a
 *   custom "On range" selection always renders its entire span, however long, with no upper limit.
 *
 * Widgets that must reflect only the selected period (not the surrounding
 * context) should not read `resolveMonths` directly — filter the resulting
 * `trendData` down with `scopeToSelectedPeriod` instead.
 */
export const resolveMonths = (
  dateRange: DateRangeValue | null | undefined,
  defaultCount: number
): string[] => {
  if (!dateRange) return buildMonths(defaultCount);

  const { preset, to } = dateRange;

  if (preset === 'current_month' || preset === 'last_month' || preset === 'month') {
    // Single month → show last `defaultCount` months ending at that month.
    const anchor = new Date(to.getFullYear(), to.getMonth(), 1);
    return Array.from({ length: defaultCount }, (_, i) => {
      const d = new Date(anchor.getFullYear(), anchor.getMonth() - (defaultCount - 1 - i), 1);
      return toYYYYMM(d);
    });
  }

  // Multi-month range (last_two_months, last_three_months, on_range) →
  // enumerate every month from `from` to `to` in full, with no clamp.
  return exactMonthsInRange(dateRange);
};

/**
 * Narrow `trendData` (which may span a wider trailing window than what's
 * selected, for chart context) down to just the exactly selected period —
 * for widgets like "High-water Mark" that must reflect the selected period,
 * not the surrounding months the chart also draws. With no range selected,
 * the wider window *is* the selected scope, so it's returned as-is.
 */
export const scopeToSelectedPeriod = (
  trendData: BillingTrendPoint[],
  dateRange: DateRangeValue | null | undefined
): BillingTrendPoint[] => {
  if (!dateRange) return trendData;
  const periodMonths = new Set(exactMonthsInRange(dateRange));
  return trendData.filter((p) => periodMonths.has(p.month));
};

/**
 * Aggregate raw (company, month) billing rows into one point per month,
 * summed across companies — carrying each company's most recently known
 * `maxUserCount` forward into any month where it has no explicit row,
 * rather than treating "no row" as "zero users that month".
 *
 * Per `docs/partner-audit-tracking.md`, the backend only writes a company's
 * billing doc for a month on that month's *first* user create/delete event.
 * A company with a perfectly stable headcount for an entire month (no
 * lifecycle events at all) gets no doc for that month — not a doc reading
 * 0, no doc whatsoever. The previous implementation summed only the rows
 * that exist, so any month sandwiched between two months that *did* have
 * events silently dropped that company's contribution to 0, undercounting
 * the aggregate even for fully closed, historical months (reported: HWM
 * read 12 for a company/period whose real provisioned total was 14).
 *
 * A company with no row anywhere in the requested window still contributes
 * 0 for the months before its first known record — there's no data to
 * carry from. Explicit rows always win over a carried value, even when
 * they're lower (a real deletion event legitimately drops the count).
 */
export const aggregateTrendData = (
  results: PartnerBillingBulkResult[],
  months: string[]
): BillingTrendPoint[] => {
  const monthSet = new Set(months);
  const byCompany = new Map<string, Map<string, number>>();

  results.forEach((result) => {
    if (typeof result.maxUserCount !== 'number' || !monthSet.has(result.billingMonth)) return;
    const companyKey = result.companyId ?? '';
    if (!byCompany.has(companyKey)) byCompany.set(companyKey, new Map());
    byCompany.get(companyKey)?.set(result.billingMonth, result.maxUserCount);
  });

  const totals = new Map<string, number>(months.map((m) => [m, 0]));
  byCompany.forEach((monthlyCounts) => {
    let carry = 0;
    let seeded = false;
    months.forEach((m) => {
      const explicit = monthlyCounts.get(m);
      if (explicit !== undefined) {
        carry = explicit;
        seeded = true;
      }
      if (seeded) {
        totals.set(m, (totals.get(m) ?? 0) + carry);
      }
    });
  });

  return months.map((m) => ({
    month: m,
    label: toLabel(m),
    maxUserCount: totals.get(m) ?? 0,
  }));
};

const toLabel = (yyyyMM: string): string => {
  const [y, m] = yyyyMM.split('-').map(Number);
  const d = new Date(y, m - 1, 1);
  const monthShort = d.toLocaleDateString('en-US', { month: 'short' });
  if (y === new Date().getFullYear()) {
    return monthShort; // e.g. "Jan"
  }
  return `${monthShort} '${String(y).slice(2)}`; // e.g. "Dec '25"
};

/**
 * Calls GET /api/v1/partner/billing/bulk?months=...&company_ids=... once for
 * every (month × company) combination needed, then aggregates maxUserCount
 * per month.
 *
 * This used to fan out one GET /api/v1/partner/billing request per
 * (month, company) pair via useQueries — for N companies and M months that's
 * N*M parallel requests, which was enough to trip the per-IP rate limit at
 * the load balancer for any reasonably sized partner. The bulk endpoint
 * fetches all of those in a single Firestore batch read server-side, so the
 * dashboard now makes exactly one HTTP call here regardless of N/M.
 *
 * @param companyIds     Company IDs to query (pass empty array → no call fired).
 * @param dateRange      Controls which months are fetched (null = last 6 months).
 * @param monthCount     Fallback window when dateRange is null (default 7 = 6 past months + current).
 * @param organizationId AceUpAdmin/AceUpViewer only — scopes the request to this organization.
 *   The backend falls back to the caller's own org_id (JWT) when omitted, which is *not* what an
 *   AceUp dashboard session wants once an org has been picked (billing periods are keyed by
 *   org + company, so the wrong org_id here means every lookup silently misses — the chart just
 *   shows flat zeros instead of erroring). Ignored (and unnecessary) for PartnerAdmin/PartnerViewer,
 *   whose org_id always comes from the JWT.
 */
export const useBillingTrendQuery = (
  companyIds: string[],
  dateRange?: DateRangeValue | null,
  monthCount = 6,
  organizationId?: string | null
): { trendData: BillingTrendPoint[]; isLoading: boolean } => {
  const months = resolveMonths(dateRange, monthCount);

  const { data, isLoading } = useBasicQuery<PartnerBillingBulkResponse>(
    PARTNER_BILLING_BULK_PATH,
    {
      months: months.join(','),
      company_ids: companyIds.length > 0 ? companyIds.join(',') : undefined,
      organization_id: organizationId || undefined,
    },
    // Preserves prior behavior: no companies known yet → don't fire at all.
    { enabled: companyIds.length > 0 }
  );

  const trendData = aggregateTrendData(data?.results ?? [], months);

  return { trendData, isLoading };
};
